import React from "react";
import { Handle, Position, NodeProps } from "@xyflow/react";
import { AutomationNodeType } from "../../services/automationService";

export type WorkflowBlockDef = {
  type: AutomationNodeType;
  label: string;
  description: string;
  category: "flow" | "source" | "action" | "message";
  defaults?: Record<string, any>;
};

export const WORKFLOW_BLOCKS: WorkflowBlockDef[] = [
  {
    type: "start",
    label: "Start",
    description: "Workflow entry point",
    category: "flow",
  },
  {
    type: "end",
    label: "End",
    description: "Workflow exit point",
    category: "flow",
  },
  {
    type: "lead_source",
    label: "Lead Source",
    description: "Social, portal, or bulk upload",
    category: "source",
    defaults: { sources: [], sourceGroup: "social" },
  },
  {
    type: "lead_capture",
    label: "Lead Capture",
    description: "New lead captured",
    category: "source",
  },
  {
    type: "smart_routing",
    label: "Smart Routing",
    description: "Round-robin team assignment",
    category: "action",
    defaults: {
      routingMode: "round_robin",
      assignTarget: "project",
      teamRole: "sales",
      teamRoles: [],
      teamLabel: "Sales Team",
      departmentId: "",
      departmentName: "",
      departmentIds: [],
      teamIds: [],
      userIds: [],
      projectIds: [],
      locations: [],
    },
  },
  {
    type: "masked_call",
    label: "Masked Call",
    description: "Call with mobile number masking",
    category: "action",
    defaults: { maskingEnabled: true },
  },
  {
    type: "stage_action",
    label: "Stage Action",
    description: "On stage change — run one or more actions",
    category: "action",
    defaults: {
      triggerOn: "enter_stage",
      triggerStage: "",
      stageKey: "",
      stageLabel: "",
      autoMessage: true,
      actions: [
        {
          id: "act_1",
          type: "send_whatsapp",
          actionKey: "thank_you",
          templateWid: "",
          templateName: "",
        },
      ],
    },
  },
  {
    type: "multi_action",
    label: "Multi Actions",
    description: "Stage condition + multiple actions in one block",
    category: "action",
    defaults: {
      triggerOn: "enter_stage",
      triggerStage: "",
      actions: [
        {
          id: "act_1",
          type: "send_whatsapp",
          actionKey: "thank_you",
          label: "Thank you WhatsApp",
          templateWid: "",
          templateName: "",
        },
      ],
    },
  },
  {
    type: "follow_up",
    label: "Follow-up",
    description: "Follow up with customer",
    category: "action",
    defaults: { followUpType: "sales" },
  },
  {
    type: "feedback",
    label: "Feedback Required",
    description: "Collect feedback from lead",
    category: "message",
    defaults: {
      required: true,
      templateWid: "",
      templateName: "",
      feedbackFormId: "",
      actionKey: "feedback",
      feedbackUrlVariable: "1",
      feedbackUrlMode: "path",
    },
  },
  {
    type: "send_whatsapp",
    label: "Send WhatsApp",
    description: "WhatsApp template — optional stage trigger",
    category: "message",
    defaults: {
      actionKey: "thank_you",
      templateWid: "",
      templateName: "",
      channel: "whatsapp",
      triggerOn: "flow",
      triggerStage: "",
    },
  },
  {
    type: "send_email",
    label: "Send Email",
    description: "Email message",
    category: "message",
  },
  {
    type: "send_sms",
    label: "Send SMS",
    description: "SMS message",
    category: "message",
  },
  {
    type: "opportunity",
    label: "Opportunity Won/Lost",
    description: "Close as won or lost",
    category: "action",
    defaults: { outcomes: ["won", "lost"] },
  },
  {
    type: "condition",
    label: "Condition",
    description: "Branch by rule",
    category: "flow",
  },
  {
    type: "ivr_call",
    label: "IVR Call",
    description: "Trigger IVR call",
    category: "action",
  },
];

export const blockMeta = (type?: string) =>
  WORKFLOW_BLOCKS.find((b) => b.type === type) || WORKFLOW_BLOCKS[0];

/** Stroke icons — no fill backgrounds */
export const BlockIcon: React.FC<{
  type: string;
  className?: string;
}> = ({ type, className = "h-4 w-4" }) => {
  const common = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  switch (type) {
    case "start":
      return (
        <svg {...common}>
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
      );
    case "end":
      return (
        <svg {...common}>
          <rect x="6" y="6" width="12" height="12" rx="1.5" />
        </svg>
      );
    case "lead_source":
    case "lead_capture":
      return (
        <svg {...common}>
          <path d="M12 3v12" />
          <path d="M8 11l4 4 4-4" />
          <path d="M5 19h14" />
        </svg>
      );
    case "smart_routing":
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="12" cy="18" r="2" />
          <path d="M8 7l3 9M16 7l-3 9" />
        </svg>
      );
    case "masked_call":
    case "ivr_call":
      return (
        <svg {...common}>
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.8 19.8 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.8 19.8 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
        </svg>
      );
    case "multi_action":
    case "stage_action":
    case "stage_trigger":
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h10M4 18h7" />
          <path d="M15 15l3 3 4-5" />
        </svg>
      );
    case "follow_up":
      return (
        <svg {...common}>
          <path d="M17 2l4 4-4 4" />
          <path d="M3 11v-1a4 4 0 014-4h14" />
          <path d="M7 22l-4-4 4-4" />
          <path d="M21 13v1a4 4 0 01-4 4H3" />
        </svg>
      );
    case "feedback":
      return (
        <svg {...common}>
          <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4z" />
        </svg>
      );
    case "send_whatsapp":
      return (
        <svg {...common}>
          <path d="M21 11.5a8.5 8.5 0 01-12.4 7.5L3 21l2.1-5.4A8.5 8.5 0 1121 11.5z" />
        </svg>
      );
    case "send_email":
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case "send_sms":
      return (
        <svg {...common}>
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <path d="M12 18h.01" />
        </svg>
      );
    case "opportunity":
      return (
        <svg {...common}>
          <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
        </svg>
      );
    case "condition":
      return (
        <svg {...common}>
          <path d="M12 3l8 9-8 9-8-9 8-9z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
      );
  }
};

export type WorkflowNodeData = {
  type: AutomationNodeType;
  label: string;
  [key: string]: any;
};

export const WorkflowFlowNode: React.FC<NodeProps> = ({ data, selected }) => {
  const blockType = String((data as WorkflowNodeData)?.type || "lead_capture");
  const meta = blockMeta(blockType);
  const label = String((data as WorkflowNodeData)?.label || meta.label);
  const isStart = blockType === "start";
  const isEnd = blockType === "end";
  const sources = (data as WorkflowNodeData)?.sources;
  const triggerStage = String(
    (data as WorkflowNodeData)?.triggerStage ||
      (data as WorkflowNodeData)?.stageLabel ||
      (data as WorkflowNodeData)?.stageKey ||
      ""
  );
  const actions = Array.isArray((data as WorkflowNodeData)?.actions)
    ? (data as WorkflowNodeData).actions
    : [];
  const templateHint =
    (data as WorkflowNodeData)?.templateName ||
    ((data as WorkflowNodeData)?.templateWid === "" &&
    ["send_whatsapp", "feedback"].includes(blockType)
      ? "Template TBD"
      : "");

  return (
    <div
      className={`min-w-[200px] max-w-[240px] rounded-xl border bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] ${
        selected
          ? "border-[#1E3A5F] ring-2 ring-[#1E3A5F]/20"
          : "border-[#E5E7EB]"
      }`}
    >
      {!isStart && (
        <Handle
          type="target"
          position={Position.Left}
          className="!h-2.5 !w-2.5 !border-2 !border-white !bg-[#1E3A5F]"
        />
      )}
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <span className="mt-0.5 shrink-0 text-[#1E3A5F]">
          <BlockIcon type={blockType} className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-snug text-[#111827]">
            {label}
          </p>
          <p className="mt-0.5 text-[11px] text-[#6B7280]">{meta.label}</p>
          {triggerStage ? (
            <p className="mt-1 text-[10px] font-medium text-[#1E3A5F]">
              When stage: {triggerStage}
            </p>
          ) : null}
          {actions.length ? (
            <p className="mt-0.5 text-[10px] text-[#9CA3AF]">
              {actions.length} action{actions.length === 1 ? "" : "s"}
              {actions
                .slice(0, 2)
                .map((a: any) => ` · ${a.type?.replace(/_/g, " ") || "action"}`)
                .join("")}
              {actions.length > 2 ? "…" : ""}
            </p>
          ) : null}
          {templateHint ? (
            <p className="mt-1 text-[10px] text-[#9CA3AF]">{templateHint}</p>
          ) : null}
          {blockType === "smart_routing" ? (
            <p className="mt-1 text-[10px] font-medium text-[#1E3A5F]">
              {(data as WorkflowNodeData)?.assignTarget === "department"
                ? "Assign by department"
                : (data as WorkflowNodeData)?.assignTarget === "member" ||
                    (data as WorkflowNodeData)?.assignTarget === "person"
                  ? "Assign to selected members"
                  : (data as WorkflowNodeData)?.assignTarget === "team"
                    ? "Assign to selected teams"
                    : "Assign to the project's team"}
            </p>
          ) : null}
          {blockType === "lead_source" &&
          (!Array.isArray(sources) || sources.length === 0) ? (
            <p className="mt-1 text-[10px] font-medium text-[#1E3A5F]">
              All sources
            </p>
          ) : null}
          {Array.isArray(sources) && sources.length ? (
            <p className="mt-1 text-[10px] leading-snug text-[#9CA3AF]">
              {sources.join(" · ")}
            </p>
          ) : null}
        </div>
      </div>
      {!isEnd && (
        <Handle
          type="source"
          position={Position.Right}
          className="!h-2.5 !w-2.5 !border-2 !border-white !bg-[#1E3A5F]"
        />
      )}
    </div>
  );
};
